import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { environment } from 'src/environments/environment';
import { GroupBy } from '../_enums/groupby';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { AdherentModel } from '../_models/adherent';
import { CustomIndustryRequest } from '../_models/customIndustryRequest';
import { EnergyProfile } from '../_models/energyProfile';
import { Family } from '../_models/family';
import { Industrie } from '../_models/industrie';
import { MakeModel } from '../_models/makeModel';
import { UserEntity } from '../_models/userEntity';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { FunctionalityCode } from '../_enums/functionality.code';
import { FamilyCodes } from '../_enums/family.code';

@Injectable({
  providedIn: 'root'
})
export class IndustrieService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService
  ) {}

  getAll(groupBy?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;

    return this.http.get<Industrie[]>(`${environment.apiUrl}/industries`, { params: params, headers: this.authService.headers });
  }

  get(resourceId: string) {
    return this.http.get<Industrie>(`${environment.apiUrl}/industries/${resourceId}`, { headers: this.authService.headers });
  }

  duplicate(resourceId: string, code: string) {
    return this.http.post(`${environment.apiUrl}/industries/duplicate?industryToDuplicateId=${resourceId}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  update(industrie: Industrie) {
    return this.http.put<Industrie>(`${environment.apiUrl}/industries/${industrie.resourceId}`, industrie, { headers: this.authService.headers });
  }

  add(industrie: Industrie) {
    return this.http.post<Industrie>(`${environment.apiUrl}/industries`, industrie, { headers: this.authService.headers });
  }

  delete(resourceId: string) {
    return this.http.delete(`${environment.apiUrl}/industries/${resourceId}`, { headers: this.authService.headers });
  }

  getIndustriesFormula() {
    return this.http.get<Industrie[]>(`${environment.apiUrl}/industries/IndustriesFormula`, { headers: this.authService.headers });
  }

  getIndustriesFamily() {
    return this.http.get<Family[]>(`${environment.apiUrl}/industries/Families`, { headers: this.authService.headers });
  }

  getMakeModelsForFamily(familyId?: string) {
    return this.http.get<MakeModel[]>(`${environment.apiUrl}/industries/MakeModels/${familyId}`, { headers: this.authService.headers });
  }

  getEUsToShareIndustry(resourceId: string) {
    return this.http.get<AdherentModel[]>(`${environment.apiUrl}/industries/EusToShare/${resourceId}`, { headers: this.authService.headers });
  }

  shareIndustry(resourceId: string, eus: UserEntity[]) {
    return this.http.put<Industrie>(`${environment.apiUrl}/industries/share/${resourceId}`, eus, { headers: this.authService.headers });
  }

  exportIndustries() {
    return this.http.get<Blob>(`${environment.apiUrl}/industries/exportExcel/xlsx`, { headers: this.authService.headers, responseType: 'blob' as 'json' });
  }

  createCustomIndustryRequest(industryId: string, excelFile: File, pdfFile: File, baseInfo?: any) {
    const formData = new FormData();
    formData.append('industryId', industryId);
    formData.append('excelFile', excelFile);
    formData.append('pdfFile', pdfFile);
    if (baseInfo) {
      formData.append('code', baseInfo.code ?? '');
      formData.append('displayName', baseInfo.displayName ?? '');
      if (baseInfo.familyId) formData.append('familyId', baseInfo.familyId);
      formData.append('addressL3', baseInfo.addressL3 ?? '');
      formData.append('addressL4', baseInfo.addressL4 ?? '');
      formData.append('addressL5', baseInfo.addressL5 ?? '');
      formData.append('addressPC', baseInfo.addressPC ?? '');
      formData.append('addressLocation', baseInfo.addressLocation ?? '');
      if (baseInfo.mobileStation != null) formData.append('mobileStation', String(baseInfo.mobileStation));
    }
    return this.http.post(`${environment.apiUrl}/industries/custom`, formData, { headers: this.authService.headers });
  }

  // validate the Excel + PDF only (no persistence) — used before creating a new custom industry
  validateCustomFile(excelFile: File, pdfFile: File) {
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('pdfFile', pdfFile);
    return this.http.post(`${environment.apiUrl}/industries/custom/validate`, formData, { headers: this.authService.headers });
  }

  cancelCustomIndustryRequest(requestId: string) {
    return this.http.delete(`${environment.apiUrl}/industries/custom/${requestId}`, { headers: this.authService.headers });
  }

  getPendingCustomIndustryRequests() {
    return this.http.get<CustomIndustryRequest[]>(`${environment.apiUrl}/industries/custom/pending`, { headers: this.authService.headers });
  }

  getCustomIndustryRequestsHistory() {
    return this.http.get<CustomIndustryRequest[]>(`${environment.apiUrl}/industries/custom/history`, { headers: this.authService.headers });
  }

  // full detail of one custom request (includes the before -> after base-info diff)
  getCustomIndustryRequest(requestId: string) {
    return this.http.get<CustomIndustryRequest>(`${environment.apiUrl}/industries/custom/${requestId}`, { headers: this.authService.headers });
  }

  validateCustomIndustryRequest(requestId: string) {
    return this.http.post(`${environment.apiUrl}/industries/custom/${requestId}/validate`, null, { headers: this.authService.headers });
  }

  rejectCustomIndustryRequest(requestId: string, comment: string) {
    return this.http.post(`${environment.apiUrl}/industries/custom/${requestId}/reject`, { comment }, { headers: this.authService.headers });
  }

  isPlantMixInUse(versionId?: string) {
    let params = new HttpParams();
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.get<boolean>(`${environment.apiUrl}/industries/plant-mix-in-use`, { params: params, headers: this.authService.headers });
  }

  getEnergyProfile(industryId: string) {
    return this.http.get<EnergyProfile>(`${environment.apiUrl}/industries/${industryId}/energy-profile`, { headers: this.authService.headers });
  }

  // download the current custom justification file (kind = 'excel' | 'pdf') as a blob
  getCustomFile(industryId: string, kind: 'excel' | 'pdf') {
    return this.http.get(`${environment.apiUrl}/industries/${industryId}/custom-file/${kind}`, {
      headers: this.authService.headers,
      responseType: 'blob'
    });
  }

  // admin action — revert a validated custom plant back to the default fiche
  forceDefaultIndustry(industryId: string) {
    return this.http.post(`${environment.apiUrl}/industries/${industryId}/force-default`, null, { headers: this.authService.headers });
  }

  getNavConfig(props: NavListCommonProps) {
    if (this.authService.accessesValues.includes(FunctionalityCode.ACUView)) {
      return this.getAcuNavConfig(props);
    }
    return this.getDefaultNavConfig(props);
  }

  // The default / custom / pending split only concerns the coated-industry family (enrobés) — other
  // industry families stay flat. For the coated family, the three categories are ALWAYS shown, even
  // when empty (an empty category has no chevron -> not expandable), per PO (Tom, #943).
  private buildFamilyValue(plants: any[], familyCode?: string): any[] {
    if (familyCode !== FamilyCodes.CoatedIndustry) {
      return plants;
    }

    const isPending = (p: any) => p.customStatus === 'pending';
    // isCustom = the plant's effective values are an approved custom override (stays true while a
    // re-modification pends). A default plant being customised but never validated is NOT custom yet.
    const runsOnCustom = (p: any) => p.isCustom || p.customStatus === 'validated';
    // still on its default values : not customised in effect, and it has usable values (a born-custom
    // plant never validated has none -> isSelectable === false, so it belongs only to "pending")
    const runsOnDefault = (p: any) => !runsOnCustom(p) && p.isSelectable !== false;

    const pending = plants.filter(isPending);
    return [
      { key: this.translate.instant('industrie_nav_group_default'), type: 2, isCategory: true, value: plants.filter(runsOnDefault) },
      { key: this.translate.instant('industrie_nav_group_custom'), type: 2, isCategory: true, value: plants.filter(runsOnCustom) },
      { key: `${this.translate.instant('industrie_nav_group_pending')} (${pending.length})`, type: 2, isCategory: true, value: pending }
    ];
  }

  private getAcuNavConfig({ perimeterId }: NavListCommonProps) {
    // ACU view: industries stay in their normal "usine d'enrobés" family. The pastille is the
    // standard provenance badge (SEVE vs user), like the default view.
    return {
      header: {
        title: 'ressource_list_title_industries',
        actions: this.getHeaderActions(perimeterId),
        filters: [] as { label: string; value: string }[]
      },
      getDataSource: (_groupBy?: GroupBy) =>
        this.getAll(GroupBy.family).pipe(
          map((data: any[]) =>
            (data || []).map((node: any) => {
              const plants = (node.childs || []).map((child: any) => {
                const grandChilds = (child.childs || []).filter((c: any) => c.displayName);
                return {
                  key: child.displayName,
                  displayName: child.displayName,
                  value: grandChilds,
                  type: grandChilds.length > 0 ? 2 : undefined, //childless plant stays a leaf so it keeps its badge
                  industryId: child.industryId,
                  ownerId: child.ownerId,
                  // type 2 -> rendered by the has-child tree node, which links via node.url (not itemConfig.url),
                  // so the url must live on the node itself, same as getDefaultNavConfig
                  url: child.industryId ? `${SiteMapUrl.MenuIndustry}/${child.industryId}` : undefined,
                  isCustom: child.isCustom,
                  customStatus: child.customStatus,
                  isSelectable: child.isSelectable
                };
              });
              return { familyId: node.familyId, key: node.displayName, type: 1, value: this.buildFamilyValue(plants, node.familyCode) };
            })
          )
        ),
      getItemConfig: (node: any) => ({
        displayName: node.displayName || '',
        url: node.industryId ? `${SiteMapUrl.MenuIndustry}/${node.industryId}` : `${SiteMapUrl.MenuFormulaIndustry}/${node.resourceId}`,
        // green = SEVE base, blue = created by a user (provenance, via ownerId)
        itemType: 'industry',
        state: { resourceId: node.resourceId, perimeterId }
      })
    };
  }

  private getHeaderActions(perimeterId: string) {
    var canExportIndustries = this.authService.accessesValues.includes(FunctionalityCode.ExportIndustries);
    var actions = [
      {
        label: 'ressource_list_action_create_industry',
        onClick: () => {
          this.router.navigate([`${SiteMapUrl.MenuIndustry}/new`, 0], { state: { perimeterId } });
        }
      }
    ];
    if (canExportIndustries) {
      actions.push({
        label: 'ressource_list_action_export_industries',
        onClick: () => {
          this.exportIndustries().subscribe((file) => {
            const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([file], { type: contentType });
            let downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.setAttribute('download', 'Export - Industries.xlsx');
            document.body.appendChild(downloadLink);
            downloadLink.click();
          });
        }
      });
    }
    return actions;
  }

  private getDefaultNavConfig({ perimeterId }: NavListCommonProps) {
    return {
      header: {
        title: 'ressource_list_title_industries',
        actions: this.getHeaderActions(perimeterId),
        filters: [
          { label: 'ressource_list_filter_by_family', value: GroupBy.family },
          { label: 'ressource_list_filter_by_favorite', value: GroupBy.favorite }
        ],
        defaultFilter: GroupBy.family
      },
      getDataSource: (groupBy?: GroupBy) =>
        this.getAll(groupBy).pipe(
          map((data: any[]) =>
            data.map((node: any) => {
              const plants = node.childs.map((child: any) => {
                const grandChilds = child.childs.filter((c: any) => c.displayName);
                return {
                  key: child.displayName,
                  displayName: child.displayName,
                  value: grandChilds,
                  type: grandChilds.length > 0 ? 2 : undefined, //childless plant stays a leaf so it keeps its badge
                  industryId: child.industryId,
                  ownerId: child.ownerId,
                  isCustom: child.isCustom,
                  customStatus: child.customStatus,
                  isSelectable: child.isSelectable,
                  url: `industrie/${child.industryId}`
                };
              });
              return { familyId: node.familyId, key: node.displayName, type: 1, value: this.buildFamilyValue(plants, node.familyCode) };
            })
          )
        ),
      getItemConfig: (node: Industrie & { industryId?: string }) => ({
        displayName: node.displayName || '',
        showBadge: !node.industryId,
        url: node.industryId ? `${SiteMapUrl.MenuIndustry}/${node.industryId}` : `${SiteMapUrl.MenuFormulaIndustry}/${node.resourceId}`,
        // green = SEVE base, blue = created by a user (provenance, via ownerId)
        itemType: 'industry',
        state: { resourceId: node.resourceId, perimeterId }
      })
    };
  }
}
